import { useEffect, useState } from 'react';
import { Modal, TextInput, Button, Stack, Group } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { partnersApi } from '../../services/api/partners';
import type { Partner, UpdatePartnerRequest } from '../../types';
import { formValidators } from '../../utils/validation';

interface EditPartnerModalProps {
  opened: boolean;
  onClose: () => void;
  partner: Partner;
  onSaved: (partner: Partner) => void;
}

export function EditPartnerModal({ opened, onClose, partner, onSaved }: EditPartnerModalProps) {
  const [loading, setLoading] = useState(false);

  const form = useForm<UpdatePartnerRequest>({
    initialValues: { name: partner.name },
    validate: {
      name: formValidators.required('Partner name'),
    },
  });

  // Re-seed the form whenever the modal opens with a (possibly updated) partner
  useEffect(() => {
    if (opened) {
      form.setValues({ name: partner.name });
      form.resetDirty({ name: partner.name });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, partner.id, partner.name]);

  const handleSubmit = async (values: UpdatePartnerRequest) => {
    try {
      setLoading(true);
      const updated = await partnersApi.update(partner.id, values);
      notifications.show({
        title: 'Success',
        message: 'Partner updated successfully',
        color: 'green',
      });
      onSaved(updated);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update partner',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Edit Partner" size="md">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Partner Name"
            placeholder="e.g., Acme Corporation"
            required
            {...form.getInputProps('name')}
          />

          <TextInput
            label="Partner Code"
            value={partner.code ?? ''}
            description="Partner code cannot be changed after creation."
            disabled
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
